import React, { useEffect, useRef, useState } from 'react'
import { ProgressBar } from 'react-bootstrap'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button } from '../../../Button/Button'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetFileByIdQuery, useUploadFileRequestMutation, uploadFileWithProgress } from '../../../../store/media/file'
import { useUpdateFileMutation } from '../../../../store/media/media'
import { FileKind, FileStatus } from '../../../../models/File'
import { humanFileSize } from '../../../../common/util'
import { stripImageMetadata } from '../../../../common/fileUtils'
import { contentTypesForProfile, MAX_WORKFLOW_ASSET_BYTES } from './mediaFileProfiles'
import { RawReferenceFallback } from './RawReferenceFallback'
import type { ZocksReferenceWidgetProps } from './registry'

interface MediaFileUploaderProps extends ZocksReferenceWidgetProps {
  contentTypes: string[]
}

const noopSetFieldUploading = () => {}

const MediaFileUploader: React.FC<MediaFileUploaderProps> = ({
  value,
  onChange,
  disabled,
  label,
  contentTypes,
  id,
  setFieldUploading = noopSetFieldUploading,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)

  const [triggerUploadFileRequest] = useUploadFileRequestMutation()
  const [triggerUpdateFile] = useUpdateFileMutation()

  const { currentData: fileInfo, isFetching, isError, refetch } = useGetFileByIdQuery(value ? { accountId, fileId: value } : skipToken)

  const accept = [...contentTypes].sort().join(',')

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const triggerBrowse = () => {
    setError(null)
    resetInput()
    fileInputRef.current?.click()
  }

  const handleFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const pickedFile = event.target.files?.[0]
    resetInput()
    if (!pickedFile) {
      return
    }

    setError(null)

    if (pickedFile.size > MAX_WORKFLOW_ASSET_BYTES) {
      setError(`File is larger than ${humanFileSize(MAX_WORKFLOW_ASSET_BYTES)}.`)
      return
    }

    if (!contentTypes.includes(pickedFile.type)) {
      setError('This file type is not supported.')
      return
    }

    setIsUploading(true)
    setProgress(0)
    setFieldUploading(id, true)

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const file = await stripImageMetadata(pickedFile)
      const { data } = await triggerUploadFileRequest({
        accountId,
        body: { fileName: file.name, fileSize: file.size, contentType: file.type, kind: FileKind.WORKFLOW_ASSET },
      }).unwrap()

      if (abortController.signal.aborted) {
        return
      }

      await uploadFileWithProgress(data.fileUploadUrl, data.fileUploadParameters, file, setProgress, abortController.signal)

      if (abortController.signal.aborted) {
        return
      }

      await triggerUpdateFile({ accountId, fileId: data.fileInfo.fileId, body: { status: FileStatus.UPLOADED } }).unwrap()

      if (abortController.signal.aborted) {
        return
      }

      onChange(data.fileInfo.fileId)
    } catch (thrown) {
      const wasAborted = abortController.signal.aborted || (thrown instanceof DOMException && thrown.name === 'AbortError')
      if (!wasAborted) {
        setError('Upload failed. Please try again.')
      }
    } finally {
      abortControllerRef.current = null
      if (!abortController.signal.aborted) {
        setIsUploading(false)
        setFieldUploading(id, false)
      }
    }
  }

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      setFieldUploading(id, false)
    }
  }, [id, setFieldUploading])

  const handleRemove = () => {
    setError(null)
    onChange(undefined)
  }

  return (
    <div>
      <input ref={fileInputRef} id={id} type='file' accept={accept} disabled={disabled} hidden onChange={handleFilePicked} />

      {isUploading ? (
        <div className='d-flex flex-column gap-2'>
          <span className='paragraph-small'>Uploading… {progress}%</span>
          <ProgressBar now={progress} />
        </div>
      ) : value && fileInfo ? (
        <div className='d-flex align-items-center gap-3'>
          <div>
            <div className='fw-bold'>{fileInfo.fileName}</div>
            <div className='paragraph-small text-muted'>{humanFileSize(fileInfo.fileSize)}</div>
          </div>
          <Button type='button' variant='link' disabled={disabled} onClick={triggerBrowse}>
            Replace
          </Button>
          <Button type='button' variant='link' disabled={disabled} onClick={handleRemove}>
            Remove
          </Button>
        </div>
      ) : value && isFetching ? (
        <span className='paragraph-small text-muted'>Loading file…</span>
      ) : value && isError ? (
        <div className='d-flex align-items-center gap-3'>
          <span className='paragraph-small text-danger'>Couldn't load this file</span>
          <Button type='button' variant='link' disabled={disabled} onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : value ? (
        <div className='d-flex align-items-center gap-3'>
          <span className='paragraph-small text-danger'>File unavailable</span>
          <Button type='button' variant='link' disabled={disabled} onClick={triggerBrowse}>
            Replace
          </Button>
        </div>
      ) : (
        <Button type='button' variant='primary' disabled={disabled} onClick={triggerBrowse} aria-label={`Upload file for ${label}`}>
          Upload file
        </Button>
      )}

      {error ? <div className='paragraph-small text-danger mt-1'>{error}</div> : null}
    </div>
  )
}

export const MediaFileWidget: React.FC<ZocksReferenceWidgetProps> = (props) => {
  const subType = typeof props.config.subType === 'string' ? props.config.subType : ''
  const contentTypes = contentTypesForProfile(subType)

  if (!contentTypes) {
    const reason = subType ? `Unknown media-file profile "${subType}"` : 'Missing media-file profile'
    return <RawReferenceFallback {...props} reason={reason} />
  }

  return <MediaFileUploader {...props} contentTypes={contentTypes} />
}
